/**
 * What this guide said before.
 *
 * A handbook is edited by whoever is closest to the work, which is exactly the
 * situation where a paragraph that mattered disappears in a hurry. Every save
 * writes the previous text (see the API's manage.routes.ts), and this is where
 * somebody reads it back and, if it was better, puts it back.
 *
 * Restoring is an ordinary save: it writes a revision of the CURRENT text
 * first, so undoing an undo is one more click rather than a lost paragraph.
 */
import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { LoadingState } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { getGuide, listGuideRevisions, updateGuide, type KbArticle, type KbRevision } from "../../../lib/kb";
import { Btn, Modal, fmtDateTime, useToast } from "../../ui";

export function RevisionsModal({
  article,
  onClose,
  onRestored,
}: {
  article: KbArticle;
  onClose: () => void;
  /** Hands the refreshed article back, so the page under the dialog is not stale. */
  onRestored: (next: KbArticle) => void;
}) {
  const t = useT();
  const toast = useToast();
  const [rows, setRows] = useState<KbRevision[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listGuideRevisions(article.id)
      .then((r) => setRows(r.revisions))
      .catch(() => setRows([]));
  }, [article.id]);

  async function restore(rev: KbRevision) {
    setBusy(true);
    try {
      await updateGuide(article.id, {
        title: rev.title,
        body: rev.body,
        note: t("kb.rev.restoreNote", { version: rev.version }),
      });
      toast(t("kb.rev.restored"));
      // Re-fetch rather than patch: the restored body changes the contents rail,
      // the reading time and which picture slots exist, and all three are
      // computed by the server. Half-updating them here is how two of the three
      // end up disagreeing with the text on screen.
      onRestored(await getGuide(article.slug));
      onClose();
    } catch {
      toast(t("common.error"));
      setBusy(false);
    }
  }

  return (
    <Modal title={t("kb.history")} onClose={onClose} className="crm-kb-rev">
      <div className="crm-m-body">
        {rows === null ? <LoadingState variant="inline" label={t("common.loading")} /> : null}
        {rows?.length === 0 ? <p className="crm-kb-rev-none">{t("kb.rev.none")}</p> : null}

        <ul className="crm-kb-rev-list">
          {(rows ?? []).map((rev) => (
            <li key={rev.id}>
              <div className="crm-kb-rev-row">
                <span className="crm-kb-rev-v">v{rev.version}</span>
                <span className="crm-kb-rev-when">{fmtDateTime(rev.at)}</span>
                <span className="crm-kb-rev-who">{rev.savedByName ?? t("kb.someone")}</span>
                <span className="crm-kb-rev-note">{rev.note ?? ""}</span>
                <Btn small onClick={() => setOpen(open === rev.id ? null : rev.id)}>
                  {open === rev.id ? t("kb.rev.hide") : t("kb.rev.show")}
                </Btn>
                <Btn small onClick={() => void restore(rev)} disabled={busy}>
                  <RotateCcw className="size-3.5" />
                  {t("kb.rev.restore")}
                </Btn>
              </div>
              {open === rev.id ? (
                <div className="crm-doc crm-kb-rev-body" dangerouslySetInnerHTML={{ __html: rev.body }} />
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
